import React, { useId, useState } from 'react';
import { uploadProfileImage } from '../services/profileImages';
import { useI18n } from '../i18n/I18nContext';
import { getComponentCopy } from '../i18n/componentCopy';
import '../style/ProfileImageUpload.css';

const getUploadErrorMessage = (uploadError, copy) => {
  switch (uploadError?.code) {
    case 'storage/retry-limit-exceeded':
      return copy.uploadConnection;
    case 'storage/unauthorized':
      return copy.uploadPermission;
    case 'storage/canceled':
      return copy.uploadCancelled;
    default:
      return copy.uploadError;
  }
};

const ProfileImageUpload = ({
  userId,
  value,
  onChange,
  disabled = false,
  required = false,
}) => {
  const { language } = useI18n();
  const copy = getComponentCopy(language);
  const inputId = useId();
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploading(true);
    setMessage('');
    setError('');

    try {
      const downloadUrl = await uploadProfileImage({ file, userId });
      onChange(downloadUrl);
      setMessage(copy.photoReady);
    } catch (uploadError) {
      setError(getUploadErrorMessage(uploadError, copy));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="profile-image-upload">
      <span className="profile-image-upload-label">{copy.photo}</span>
      <div className="profile-image-upload-row">
        <div className="profile-image-upload-preview" aria-hidden={!value}>
          {value ? <img src={value} alt={copy.preview} /> : <span>{copy.noPhoto}</span>}
        </div>
        <div className="profile-image-upload-controls">
          <label className={`profile-image-upload-button ${disabled || uploading ? 'disabled' : ''}`} htmlFor={inputId}>
            {uploading ? copy.uploading : value ? copy.replacePhoto : copy.choosePhoto}
          </label>
          <input
            id={inputId}
            className="profile-image-upload-input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileChange}
            disabled={disabled || uploading}
            required={required && !value}
          />
          <small>{copy.photoHelp}</small>
        </div>
      </div>
      {message && <p className="profile-image-upload-message success" role="status">{message}</p>}
      {error && <p className="profile-image-upload-message error" role="alert">{error}</p>}
    </div>
  );
};

export default ProfileImageUpload;
